// Email Rendering Service - Gmail & Outlook API Integration
// Handles MIME parsing, base64 decoding, HTML sanitization with DOMPurify

// DOMPurify dynamic import removed for build stability in edge bundling environment
// We'll use a lightweight sanitizer fallback to remove scripts/styles and event attributes when DOMPurify is unavailable
let hasNativeDompurify = false;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const maybe = require('dompurify');
  if (maybe) hasNativeDompurify = true;
} catch (e) {
  hasNativeDompurify = false;
}

function simpleSanitize(html: string): string {
  if (!html) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    // Remove dangerous tags
    const dangerous = doc.querySelectorAll('script, style, iframe, object, embed, link');
    dangerous.forEach(el => el.remove());

    // Remove on* attributes and javascript: URLs
    const all = doc.querySelectorAll('*');
    all.forEach((el) => {
      // Remove event handlers
      [...el.attributes].forEach(attr => {
        if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
        if (attr.value && typeof attr.value === 'string' && attr.value.trim().toLowerCase().startsWith('javascript:')) {
          el.removeAttribute(attr.name);
        }
      });
    });

    return doc.body.innerHTML;
  } catch (e) {
    console.error('simpleSanitize failed', e);
    return '';
  }
}

import type { Email, Attachment } from '../types/email';

// ================ Gmail API Implementation ================

interface GmailMessage {
  id: string;
  threadId: string;
  payload: GmailPayload;
  snippet: string;
  internalDate: string;
}

interface GmailPayload {
  mimeType: string;
  headers: Array<{ name: string; value: string }>;
  body?: { data?: string; size?: number };
  parts?: GmailPayload[];
  filename?: string;
}

interface ParsedContent {
  html: string | null;
  text: string | null;
  attachments: Attachment[];
}

/**
 * Decode base64url format used by Gmail API
 * Adds padding and replaces URL-safe characters
 */
function decodeBase64Url(data: string): string {
  if (!data) return '';
  
  try {
    // Add padding if needed
    let base64 = data;
    const padding = 4 - (data.length % 4);
    if (padding !== 4) {
      base64 += '='.repeat(padding);
    }
    
    // Replace URL-safe characters
    base64 = base64.replace(/-/g, '+').replace(/_/g, '/');
    
    // Decode
    const decoded = atob(base64);
    
    // Convert to UTF-8
    return decodeURIComponent(escape(decoded));
  } catch (error) {
    console.error('Error decoding base64url:', error);
    return '';
  }
}

/**
 * Recursively parse MIME parts to extract body and attachments
 */
function parseMimeParts(parts: GmailPayload[]): ParsedContent {
  let htmlBody: string | null = null;
  let textBody: string | null = null;
  const attachments: Attachment[] = [];
  
  for (const part of parts) {
    const mimeType = part.mimeType || '';
    
    // Check for nested parts (multipart/alternative, multipart/mixed, etc.)
    if (part.parts && part.parts.length > 0) {
      const nested = parseMimeParts(part.parts);
      
      // Prefer HTML over text
      if (nested.html && !htmlBody) {
        htmlBody = nested.html;
      }
      if (nested.text && !textBody) {
        textBody = nested.text;
      }
      
      attachments.push(...nested.attachments);
      continue;
    }
    
    // Extract HTML content
    if (mimeType === 'text/html' && part.body?.data) {
      const decoded = decodeBase64Url(part.body.data);
      if (decoded && !htmlBody) {
        htmlBody = decoded;
      }
    }
    
    // Extract plain text content
    else if (mimeType === 'text/plain' && part.body?.data) {
      const decoded = decodeBase64Url(part.body.data);
      if (decoded && !textBody) {
        textBody = decoded;
      }
    }
    
    // Handle attachments (files with filename)
    else if (part.filename && part.filename.length > 0) {
      attachments.push({
        id: `att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        filename: part.filename,
        mimeType: mimeType,
        size: part.body?.size || 0,
        url: '' // Would need to fetch via Gmail API
      });
    }
  }
  
  return { html: htmlBody, text: textBody, attachments };
}

/**
 * Extract email body from Gmail message
 * Handles both single-part and multi-part MIME messages
 */
function extractGmailBody(message: GmailMessage): ParsedContent {
  const payload = message.payload;
  
  // Check if message has parts (multipart)
  if (payload.parts && payload.parts.length > 0) {
    return parseMimeParts(payload.parts);
  }
  
  // Single part message
  const mimeType = payload.mimeType || '';
  const bodyData = payload.body?.data;
  
  if (!bodyData) {
    return { html: null, text: null, attachments: [] };
  }
  
  const decoded = decodeBase64Url(bodyData);
  
  if (mimeType === 'text/html') {
    return { html: decoded, text: null, attachments: [] };
  } else {
    return { html: null, text: decoded, attachments: [] };
  }
}

/**
 * Get header value from Gmail message
 */
function getHeader(message: GmailMessage, headerName: string): string {
  const header = message.payload.headers.find(
    h => h.name.toLowerCase() === headerName.toLowerCase()
  );
  return header?.value || '';
}

// ================ Outlook API Implementation ================

interface OutlookMessage {
  id: string;
  subject: string;
  from: { emailAddress: { name: string; address: string } };
  toRecipients: Array<{ emailAddress: { name: string; address: string } }>;
  body: {
    contentType: 'html' | 'text';
    content: string;
  };
  receivedDateTime: string;
  hasAttachments: boolean;
  attachments?: Array<{
    id: string;
    name: string;
    contentType: string;
    size: number;
  }>;
}

/**
 * Extract body from Outlook message
 * Outlook provides pre-parsed content with explicit contentType
 */
function extractOutlookBody(message: OutlookMessage): ParsedContent {
  const { contentType, content } = message.body;
  
  const attachments: Attachment[] = (message.attachments || []).map(att => ({
    id: att.id,
    filename: att.name,
    mimeType: att.contentType,
    size: att.size,
    url: '' // Would need to fetch via Graph API
  }));
  
  if (contentType === 'html') {
    return { html: content, text: null, attachments };
  } else {
    return { html: null, text: content, attachments };
  }
}

// ================ HTML Sanitization ================

/**
 * Process inline styles MINIMALLY - only remove VML/MSO elements that cause errors
 * DO NOT modify widths, heights, or any other layout attributes
 * Render emails exactly as received
 */
function processInlineStyles(html: string): string {
  if (!html) return '';
  
  try {
    // Create temporary DOM for manipulation
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // ONLY remove Outlook-specific VML and MSO elements that cause JS errors
    const allEls = doc.querySelectorAll('*');
    const vmlElements: Element[] = [];
    allEls.forEach((el) => {
      const tagName = el.tagName.toLowerCase();
      const className = el.className || '';
      // Check for VML namespaces (v:, o:) or mso classes
      if (tagName.startsWith('v:') || tagName.startsWith('o:') || 
          (typeof className === 'string' && className.includes('mso'))) {
        vmlElements.push(el);
      }
    });
    vmlElements.forEach(el => el.remove());
    
    // Return the minimally modified HTML - preserve ALL original styling
    return doc.body.innerHTML;
  } catch (error) {
    console.error('Error processing inline styles:', error);
    return html; // Return original HTML on error
  }
}

/**
 * Sanitize HTML content using DOMPurify to prevent XSS attacks
 * Preserves email formatting like Outlook and Gmail while ensuring security
 */
function sanitizeHtml(html: string): string {
  if (!html) return '';
  
  try {
    // Pre-process: Fix common email HTML issues
    let processedHtml = html;
    
    // Remove Microsoft Office XML namespaces that break rendering
    processedHtml = processedHtml.replace(/xmlns:[a-z]+="[^"]*"/gi, '');
    processedHtml = processedHtml.replace(/<o:p>/gi, '<p>').replace(/<\/o:p>/gi, '</p>');
    processedHtml = processedHtml.replace(/<v:[^>]*>/gi, '').replace(/<\/v:[^>]*>/gi, '');
    
    // Configure DOMPurify to allow common email HTML elements and styling
    const config = {
      ALLOWED_TAGS: [
        // Text formatting
        'p', 'br', 'span', 'div', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins', 'sub', 'sup', 'mark', 'small', 'big',
        // Headings
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        // Lists
        'ul', 'ol', 'li', 'dl', 'dt', 'dd',
        // Links and images
        'a', 'img',
        // Tables
        'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'col', 'colgroup',
        // Sections
        'article', 'section', 'header', 'footer', 'main', 'aside', 'nav',
        // Blocks
        'blockquote', 'pre', 'code', 'hr',
        // Other
        'font', 'center'
      ],
      ALLOWED_ATTR: [
        // Styling - KEEP ALL STYLES
        'style', 'class', 'id',
        // Links
        'href', 'target', 'rel',
        // Images
        'src', 'alt', 'width', 'height',
        // Tables
        'colspan', 'rowspan', 'border', 'cellpadding', 'cellspacing', 'align', 'valign',
        // Text
        'color', 'bgcolor', 'size', 'face',
        // Accessibility
        'aria-label', 'aria-describedby', 'title',
        // Email specific
        'dir', 'lang'
      ],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
      // KEEP ALL STYLING - emails depend on inline styles
      ALLOW_DATA_ATTR: false,
      ALLOW_UNKNOWN_PROTOCOLS: false,
      SAFE_FOR_TEMPLATES: false,
      WHOLE_DOCUMENT: false,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      FORCE_BODY: false,
      // Preserve CSS - critical for email rendering
      ADD_TAGS: [],
      ADD_ATTR: []
    };
    
    // Sanitize the HTML - prefer DOMPurify when available
    let clean: string;
    try {
      if (hasNativeDompurify) {
        // Require and use DOMPurify if present
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const DOMPurify = require('dompurify');
        clean = DOMPurify.sanitize(processedHtml, config);
      } else {
        // Fallback lightweight sanitizer
        clean = simpleSanitize(processedHtml);
      }
    } catch (e) {
      console.error('Sanitization error, falling back to simpleSanitize', e);
      clean = simpleSanitize(processedHtml);
    }
    
    // Post-process: Fix inline styles that break responsive layout
    clean = processInlineStyles(clean);
    
    // Return the sanitized and processed HTML
    return clean;
  } catch (error) {
    console.error('Error sanitizing HTML:', error);
    return '<div style="color: #ef4444; padding: 1rem;">Failed to render email content safely</div>';
  }
}

/**
 * Convert plain text to safe HTML with preserved formatting
 */
function textToHtml(text: string): string {
  if (!text) return '';
  
  // Escape HTML entities
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
  
  // Convert line breaks to <br>
  const withBreaks = escaped.replace(/\n/g, '<br>');
  
  // Wrap in a div for consistent styling
  return `<div style="white-space: pre-wrap; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 1rem 0;">${withBreaks}</div>`;
}

/**
 * Remove quoted text and signatures from email content
 * Strips common email separators and previous conversation threads
 */
function removeQuotedContent(text: string): string {
  if (!text) return '';
  
  // Common patterns that indicate quoted/previous email content
  const quotedPatterns = [
    // Gmail/Outlook reply separators
    /^On .+wrote:$/im,
    /^From:.+Sent:.+To:.+Subject:/im,
    /^________________________________$/m,
    /^-----Original Message-----$/im,
    
    // Signature separators
    /^--\s*$/m,
    /^—+\s*$/m,
    /^\*\*\*+\s*$/m,
    
    // Quote indicators
    /^[>\|]+\s*.+$/gm,
    
    // Common signature starters
    /^(Sent from|Get Outlook|Cheers|Best regards|Kind regards|Sincerely|Thanks|Thank you|Regards),?$/im,
    
    // Email footers
    /^This email and any attachments/im,
    /^Confidentiality Notice:/im,
    /^DISCLAIMER:/im
  ];
  
  let cleanedText = text;
  
  // Try to find the first occurrence of quoted content
  let firstQuoteIndex = cleanedText.length;
  
  for (const pattern of quotedPatterns) {
    const match = cleanedText.match(pattern);
    if (match && match.index !== undefined && match.index < firstQuoteIndex) {
      firstQuoteIndex = match.index;
    }
  }
  
  // If we found quoted content, trim everything after it
  if (firstQuoteIndex < cleanedText.length) {
    cleanedText = cleanedText.substring(0, firstQuoteIndex);
  }
  
  // Remove lines that are clearly quotes (start with > or |)
  cleanedText = cleanedText
    .split('\n')
    .filter(line => !line.trim().match(/^[>\|]/))
    .join('\n');
  
  return cleanedText.trim();
}

/**
 * Extract plain text from HTML for snippet/preview display
 * Includes intelligent removal of quoted content and signatures
 */
export function extractPlainText(html: string): string {
  if (!html) return '';
  
  try {
    // Create a temporary DOM element
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Remove script, style, and unwanted tags
    const unwanted = temp.querySelectorAll('script, style, iframe, object, embed');
    unwanted.forEach(el => el.remove());
    
    // Remove Gmail quote blocks
    const gmailQuotes = temp.querySelectorAll('.gmail_quote, [class*="gmail_quote"]');
    gmailQuotes.forEach(el => el.remove());
    
    // Remove Outlook quote blocks
    const outlookQuotes = temp.querySelectorAll('[id*="divRplyFwdMsg"], [class*="OutlookMessageHeader"]');
    outlookQuotes.forEach(el => el.remove());
    
    // Get text content
    let text = temp.textContent || temp.innerText || '';
    
    // Remove quoted content and signatures
    text = removeQuotedContent(text);
    
    // Clean up whitespace
    return text
      .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
      .replace(/\n+/g, ' ')  // Replace newlines with spaces
      .trim();
  } catch (error) {
    console.error('Error extracting plain text:', error);
    return '';
  }
}

// ================ Public API ================

export interface RenderedEmail {
  safeHtml: string;
  hasHtmlContent: boolean;
  attachments: Attachment[];
}

/**
 * Render Gmail message with full MIME parsing and sanitization
 */
export function renderGmailMessage(message: GmailMessage): RenderedEmail {
  const parsed = extractGmailBody(message);
  
  // Prefer HTML content when available
  let safeHtml: string;
  let hasHtmlContent: boolean;
  
  if (parsed.html) {
    safeHtml = sanitizeHtml(parsed.html);
    hasHtmlContent = true;
  } else if (parsed.text) {
    safeHtml = textToHtml(parsed.text);
    hasHtmlContent = false;
  } else {
    safeHtml = '<p style="color: hsl(var(--muted));">No content available</p>';
    hasHtmlContent = false;
  }
  
  return {
    safeHtml,
    hasHtmlContent,
    attachments: parsed.attachments
  };
}

/**
 * Render Outlook message with HTML sanitization
 */
export function renderOutlookMessage(message: OutlookMessage): RenderedEmail {
  const parsed = extractOutlookBody(message);
  
  let safeHtml: string;
  let hasHtmlContent: boolean;
  
  if (parsed.html) {
    safeHtml = sanitizeHtml(parsed.html);
    hasHtmlContent = true;
  } else if (parsed.text) {
    safeHtml = textToHtml(parsed.text);
    hasHtmlContent = false;
  } else {
    safeHtml = '<p style="color: hsl(var(--muted));">No content available</p>';
    hasHtmlContent = false;
  }
  
  return {
    safeHtml,
    hasHtmlContent,
    attachments: parsed.attachments
  };
}

/**
 * Fetch and render Gmail message
 */
export async function fetchAndRenderGmail(
  accessToken: string,
  userId: string,
  messageId: string
): Promise<RenderedEmail> {
  try {
    const response = await fetch(
      `https://gmail.googleapis.com/gmail/v1/users/${userId}/messages/${messageId}?format=full`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Gmail API error: ${response.status}`);
    }
    
    const message: GmailMessage = await response.json();
    return renderGmailMessage(message);
    
  } catch (error) {
    console.error('Error fetching Gmail message:', error);
    return {
      safeHtml: '<p style="color: #ef4444;">Failed to load email content</p>',
      hasHtmlContent: false,
      attachments: []
    };
  }
}

/**
 * Fetch and render Outlook message
 */
export async function fetchAndRenderOutlook(
  accessToken: string,
  messageId: string
): Promise<RenderedEmail> {
  try {
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/me/messages/${messageId}?$select=subject,from,toRecipients,body,receivedDateTime,hasAttachments`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'outlook.body-content-type="html"'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Outlook API error: ${response.status}`);
    }
    
    const message: OutlookMessage = await response.json();
    
    // Fetch attachments if present
    if (message.hasAttachments) {
      const attachmentsResponse = await fetch(
        `https://graph.microsoft.com/v1.0/me/messages/${messageId}/attachments`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (attachmentsResponse.ok) {
        const attachmentsData = await attachmentsResponse.json();
        message.attachments = attachmentsData.value;
      }
    }
    
    return renderOutlookMessage(message);
    
  } catch (error) {
    console.error('Error fetching Outlook message:', error);
    return {
      safeHtml: '<p style="color: #ef4444;">Failed to load email content</p>',
      hasHtmlContent: false,
      attachments: []
    };
  }
}

/**
 * Sanitize existing HTML body (for legacy data)
 */
export function sanitizeEmailBody(body: string, isHtml: boolean = true): string {
  if (!body) return '';
  
  if (isHtml) {
    return sanitizeHtml(body);
  } else {
    return textToHtml(body);
  }
}

// Export types for external use
export type { GmailMessage, OutlookMessage, ParsedContent };
